import { useState, useRef, useCallback, useEffect } from 'react';
import maplibregl from 'maplibre-gl';
import { authFetch, getAuthHeaders } from '../lib/keycloak-api';

/**
 * Custom hook for managing Street View markers using GeoJSON layer
 * Markers are rendered as map layers, not HTML elements, so they're truly fixed to coordinates
 */
export const useStreetView = (mapRef) => {
  const [isStreetViewEnabled, setIsStreetViewEnabled] = useState(false);
  const fetchTimeoutRef = useRef(null);
  const lastBoundsRef = useRef(null);
  const allFeaturesRef = useRef([]); // Store all fetched features
  const popupRef = useRef(null);

  // Fetch street view data from API
  const fetchStreetViewData = useCallback(async (bounds) => {
    if (!bounds) return [];

    try {
      const north = bounds.getNorth();
      const south = bounds.getSouth();
      const east = bounds.getEast();
      const west = bounds.getWest();

      const url = `https://search-maps-pub-int.delhivery.com/streetView?top_lat=${north}&top_lon=${east}&bottom_lat=${south}&bottom_lon=${west}`;
      
      const headers = await getAuthHeaders();
      const response = await authFetch(url, {
        headers: {
          ...headers,
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.results || [];
    } catch (error) {
      return [];
    }
  }, []);

  // Add or update the street view layer
  const updateStreetViewLayer = useCallback(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    // Convert features to GeoJSON
    const geojson = {
      type: 'FeatureCollection',
      features: allFeaturesRef.current.map(result => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [result.location.lon, result.location.lat]
        },
        properties: {
          s3_uri: result.s3_uri,
          timestamp: result.timestamp,
          lat: result.location.lat,
          lon: result.location.lon
        }
      }))
    };

    // Add or update source
    if (map.getSource('street-view-points')) {
      map.getSource('street-view-points').setData(geojson);
      // Force map to re-render the layer
      map.triggerRepaint();
    } else {
      map.addSource('street-view-points', {
        type: 'geojson',
        data: geojson
      });

      // Add circle layer for the red dots
      map.addLayer({
        id: 'street-view-circles',
        type: 'circle',
        source: 'street-view-points',
        paint: {
          'circle-radius': 10.4,
          'circle-color': '#dc3545',
          'circle-opacity': 0.7,
          'circle-stroke-width': 2.6,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-opacity': 0.8
        }
      });

      // Add hover effect
      map.on('mouseenter', 'street-view-circles', () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', 'street-view-circles', () => {
        map.getCanvas().style.cursor = '';
      });

      // Add click handler
      map.on('click', 'street-view-circles', async (e) => {
        if (!e.features || e.features.length === 0) return;

        const feature = e.features[0];
        const coordinates = feature.geometry.coordinates.slice();
        const props = feature.properties;

        // Remove existing popup
        if (popupRef.current) {
          popupRef.current.remove();
        }

        // Show loading popup
        const loadingPopup = new maplibregl.Popup({
          offset: 15,
          closeButton: true,
          closeOnClick: true,
          className: 'street-view-popup',
          maxWidth: '640px'
        })
          .setLngLat(coordinates)
          .setHTML(`
            <div style="padding: 20px; text-align: center;">
              <div style="margin-bottom: 8px;">Loading image...</div>
              <div style="font-size: 11px; color: #666;">
                <strong>Location:</strong> ${parseFloat(props.lat).toFixed(6)}, ${parseFloat(props.lon).toFixed(6)}
              </div>
            </div>
          `)
          .addTo(map);

        popupRef.current = loadingPopup;

        try {
          // Call the new API to get presigned URL
          const encodedS3Uri = encodeURIComponent(props.s3_uri);
          const headers = await getAuthHeaders();
          const response = await authFetch(`https://search-maps-pub-int.delhivery.com/streetViewPresignedUrl?s3_uri=${encodedS3Uri}`, {
            headers: {
              ...headers,
            }
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          const presignedUrl = data.data.presigned_url;

          // Create unique ID for this image
          const imgId = `street-view-img-${Date.now()}`;

          // Create popup content with the fetched image (2x larger)
          const popupContent = `
            <div style="padding: 16px;">
              <div style="margin-bottom: 16px; position: relative;">
                <img 
                  id="${imgId}"
                  src="${presignedUrl}" 
                  alt="Street View" 
                  style="width: 100%; height: auto; border-radius: 8px; display: block; cursor: pointer; transition: opacity 0.2s;"
                  onmouseover="this.style.opacity='0.9'"
                  onmouseout="this.style.opacity='1'"
                  onerror="this.onerror=null; this.src='data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22600%22 height=%22400%22%3E%3Crect fill=%22%23f0f0f0%22 width=%22600%22 height=%22400%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 text-anchor=%22middle%22 dy=%22.3em%22 fill=%22%23999%22%3EImage not available%3C/text%3E%3C/svg%3E';"
                />
                <div style="position: absolute; top: 8px; right: 8px; background: rgba(0,0,0,0.6); color: white; padding: 4px 8px; border-radius: 4px; font-size: 11px; pointer-events: none;">
                  Click to expand
                </div>
              </div>
              <div style="font-size: 13px; color: #666;">
                <div style="margin-bottom: 6px;">
                  <strong>Location:</strong> ${parseFloat(props.lat).toFixed(6)}, ${parseFloat(props.lon).toFixed(6)}
                </div>
                ${props.timestamp ? `<div style="color: #999; font-size: 12px;">
                  ${new Date(props.timestamp).toLocaleString()}
                </div>` : ''}
              </div>
            </div>
          `;

          // Update popup with image
          loadingPopup.setHTML(popupContent);

          // Add click handler to image for full-screen view
          setTimeout(() => {
            const img = document.getElementById(imgId);
            if (img) {
              img.addEventListener('click', (e) => {
                e.stopPropagation();
                
                // Create full-screen overlay
                const overlay = document.createElement('div');
                overlay.style.cssText = `
                  position: fixed;
                  top: 0;
                  left: 0;
                  width: 100vw;
                  height: 100vh;
                  background: rgba(0, 0, 0, 0.95);
                  z-index: 10000;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  cursor: zoom-out;
                  animation: fadeIn 0.2s ease;
                `;

                const fullImg = document.createElement('img');
                fullImg.src = presignedUrl;
                fullImg.style.cssText = `
                  max-width: none;
                  max-height: none;
                  width: auto;
                  height: auto;
                  object-fit: none;
                  border-radius: 8px;
                  box-shadow: 0 4px 20px rgba(0,0,0,0.5);
                `;

                overlay.appendChild(fullImg);
                document.body.appendChild(overlay);

                // Close on click
                overlay.addEventListener('click', () => {
                  overlay.style.opacity = '0';
                  setTimeout(() => {
                    if (document.body.contains(overlay)) {
                      document.body.removeChild(overlay);
                    }
                  }, 200);
                });

                // Close on ESC key
                const handleEsc = (e) => {
                  if (e.key === 'Escape') {
                    overlay.style.opacity = '0';
                    setTimeout(() => {
                      if (document.body.contains(overlay)) {
                        document.body.removeChild(overlay);
                      }
                    }, 200);
                    document.removeEventListener('keydown', handleEsc);
                  }
                };
                document.addEventListener('keydown', handleEsc);
              });
            }
          }, 100);

        } catch (error) {
          // Show error in popup
          loadingPopup.setHTML(`
            <div style="padding: 40px; text-align: center; color: #dc3545;">
              <div style="margin-bottom: 16px; font-size: 16px;">Failed to load image</div>
              <div style="font-size: 13px; color: #666;">
                <strong>Location:</strong> ${parseFloat(props.lat).toFixed(6)}, ${parseFloat(props.lon).toFixed(6)}
              </div>
            </div>
          `);
        }
      });
    }

    // Force map to re-render to ensure all points are visible
    map.triggerRepaint();
  }, [mapRef]);

  // Clear street view layer
  const clearStreetViewLayer = useCallback(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    // Remove popup
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    // Remove layer and source
    if (map.getLayer('street-view-circles')) {
      map.off('mouseenter', 'street-view-circles');
      map.off('mouseleave', 'street-view-circles');
      map.off('click', 'street-view-circles');
      map.removeLayer('street-view-circles');
    }

    if (map.getSource('street-view-points')) {
      map.removeSource('street-view-points');
    }

    // Clear stored features
    allFeaturesRef.current = [];
  }, [mapRef]);

  // Update street view data based on current viewport
  const updateStreetView = useCallback(async () => {
    if (!mapRef.current || !isStreetViewEnabled) {
      return;
    }

    const map = mapRef.current;
    const bounds = map.getBounds();

    // Check if bounds have changed significantly
    if (lastBoundsRef.current) {
      const lastBounds = lastBoundsRef.current;
      const boundsChanged = 
        Math.abs(bounds.getNorth() - lastBounds.getNorth()) > 0.001 ||
        Math.abs(bounds.getSouth() - lastBounds.getSouth()) > 0.001 ||
        Math.abs(bounds.getEast() - lastBounds.getEast()) > 0.001 ||
        Math.abs(bounds.getWest() - lastBounds.getWest()) > 0.001;

      if (!boundsChanged) {
        return;
      }
    }

    lastBoundsRef.current = bounds;

    // Fetch new data
    const results = await fetchStreetViewData(bounds);

    // Add new features (avoid duplicates)
    const existingKeys = new Set(
      allFeaturesRef.current.map(f => `${f.location.lat.toFixed(6)},${f.location.lon.toFixed(6)}`)
    );

    results.forEach(result => {
      const key = `${result.location.lat.toFixed(6)},${result.location.lon.toFixed(6)}`;
      if (!existingKeys.has(key)) {
        allFeaturesRef.current.push(result);
      }
    });

    // Update the layer
    updateStreetViewLayer();
  }, [mapRef, isStreetViewEnabled, fetchStreetViewData, updateStreetViewLayer]);

  // Handle map movement
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const handleMoveEnd = () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      fetchTimeoutRef.current = setTimeout(() => {
        updateStreetView();
      }, 300);
    };

    map.on('moveend', handleMoveEnd);

    // Initial load if enabled
    if (isStreetViewEnabled) {
      updateStreetView();
    }

    return () => {
      if (map) {
        map.off('moveend', handleMoveEnd);
      }
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [mapRef, isStreetViewEnabled, updateStreetView]);

  // Clear when disabled
  useEffect(() => {
    if (!isStreetViewEnabled) {
      clearStreetViewLayer();
      lastBoundsRef.current = null;
    }
  }, [isStreetViewEnabled, clearStreetViewLayer]);

  return {
    isStreetViewEnabled,
    setIsStreetViewEnabled,
    clearStreetViewLayer,
    updateStreetView
  };
};
